import { useEffect, useRef, useState } from 'react'
import { PacketCustomSection } from '@marketing-milk/interfaces'
import { useUpdateCustomSection } from 'hooks/api/useSprintPackets'

/**
 * Whole-slide autosave for an AE-authored custom slide.
 *
 * Structurally `useNarrativeBlockSave`, with the same `focusout` latch and
 * `lastSavedRef` reconciliation, but the payload is the *whole slide* rather
 * than one field: the card holds a title input and a body input against one
 * element, and per-field patches sharing one `updatedAt` would let a
 * late-arriving body save be silently discarded by the server's drop-if-stale
 * rule. The `focusout` handler already fires on the wrapper, not per input, so
 * whole-slide saving is the natural fit.
 *
 * A whitespace-only title is a 400, so it never leaves here — the field falls
 * back to the last saved title instead.
 */
export const useCustomSectionSave = (packetID: number, section: PacketCustomSection) => {
  const [title, setTitle] = useState(section.title)
  const [text, setText] = useState(section.richText)
  const lastSavedRef = useRef({ title: section.title, richText: section.richText })
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Adopt a server-side change (another tab, a refetch) only when the AE hasn't
  // typed since the last save — never clobber in-progress edits.
  useEffect(() => {
    const saved = lastSavedRef.current
    if (section.title !== saved.title && title === saved.title) setTitle(section.title)
    if (section.richText !== saved.richText && text === saved.richText) setText(section.richText)
    lastSavedRef.current = { title: section.title, richText: section.richText }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section.title, section.richText])

  const update = useUpdateCustomSection(packetID)

  const save = (nextTitle: string, nextText: string) => {
    const saved = lastSavedRef.current
    const trimmed = nextTitle.trim()
    // Empty titles are rejected server-side; fall back rather than send one.
    const titleToSave = trimmed === '' ? saved.title : trimmed
    if (titleToSave === saved.title && nextText === saved.richText) {
      if (trimmed === '' && title !== saved.title) setTitle(saved.title)
      return
    }
    update.mutate(
      {
        id: section.id,
        dto: { title: titleToSave, richText: nextText, updatedAt: new Date().toISOString() },
      },
      {
        onSuccess: () => {
          lastSavedRef.current = { title: titleToSave, richText: nextText }
        },
      }
    )
    if (titleToSave !== nextTitle) setTitle(titleToSave)
  }

  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    const handler = (e: FocusEvent) => {
      if (!el.contains(e.relatedTarget as Node | null)) save(title, text)
    }
    el.addEventListener('focusout', handler)
    return () => el.removeEventListener('focusout', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, text, section.id])

  const dirty =
    title.trim() !== lastSavedRef.current.title || text !== lastSavedRef.current.richText

  // Unlike a narrative block, a slide always exists — so there is no "nothing
  // here yet" state to distinguish, and a saved slide simply reads "Saved".
  const indicator = update.isLoading
    ? 'Saving…'
    : update.isError
    ? 'Save failed'
    : dirty
    ? 'Unsaved'
    : 'Saved'

  return { title, setTitle, text, setText, indicator, wrapperRef }
}
